/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import * as dayjs from 'dayjs';
import { ProjectState } from './project-state.service';
import {
	ISpreadsheetException,
	ISpreadsheetValidation,
	SpreadsheetExceptionType,
	SpreadsheetInputType,
} from './spreadsheet/spreadsheet-base';
import { SpreadsheetService } from './spreadsheet/spreadsheet.service';
import {
	ExternalScheduleSpreadsheet,
	IExternalScheduleTicketData,
} from './spreadsheet/external-schedule-spreadsheet';
import { IProjectTicketListMetadata, ProjectTicketsService } from './project-tickets.service';
import { TicketType } from '@touchplan/common';
import { TicketFields } from '@touchplan/common';
import { dateTimeFormat } from '../plain-javascript/background-scripts/fbdb-time';
import { analyticsEventsService } from '../plain-javascript/services/analytics/AnalyticsEventsService';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';

import firebase from 'firebase/compat/app';

@Injectable()
export class ExternalScheduleService {
	private readonly EXCEPTION_DATE_FORMAT = 'YYYY-MM-DD HH:MM';

	public exception$: Subject<ISpreadsheetException>;
	public processComplete$: Subject<number>;

	private ticketsToUpdate: IExternalScheduleTicketData[];
	private spreadsheet: ExternalScheduleSpreadsheet;
	private destroy$: Subject<void>;
	private logger: ILoggerService;

	constructor(
		private projectState: ProjectState,
		private spreadsheetService: SpreadsheetService,
		private projectTicketsService: ProjectTicketsService
	) {
		this.logger = LoggerServiceFactory.create('ExternalScheduleService');
	}

	/*
	 * validate the spreadsheet and return validation info
	 * e.g.
	 * success { pass: true }
	 * fail { pass: false, message: 'Missing required column(s): Touchplan Finish' }
	 */
	public validate(fileBinary: string): ISpreadsheetValidation {
		if (!this.spreadsheet) {
			this.spreadsheet = this.spreadsheetService.getNewExternalScheduleSpreadsheet();
			this.exception$ = new Subject<ISpreadsheetException>();
			this.processComplete$ = new Subject<number>();
			this.destroy$ = new Subject<void>();
			this.ticketsToUpdate = [];

			/* spreadsheet processing has mix of sync & async (ticket stream) operations, it relies observables to emit data and notify UI */
			this.spreadsheet.exception$
				.pipe(
					map((exception) => {
						switch (exception.type) {
							case SpreadsheetExceptionType.MissingId:
								exception.message = this.formatMissingIdMessage(exception.data);
								break;
							case SpreadsheetExceptionType.DuplicateId:
								exception.message = this.formatDuplicateIdMessage(exception.data);
								break;
						}
						return exception;
					})
				)
				.subscribe(this.exception$);
			this.spreadsheet.processComplete$.subscribe(this.processComplete$);
		}
		return this.spreadsheet.validate(fileBinary, SpreadsheetInputType.Binary);
	}

	// process the spreadsheet and returns the match count
	public process(): void {
		const self = this;
		// get data from spreadsheet to update ticket.externalScheduleFinishDate on stream completion
		this.spreadsheet.ticketToUpdate$.subscribe((data: IExternalScheduleTicketData) =>
			this.ticketsToUpdate.push(data)
		);

		this.spreadsheet.prepare();
		this.getTicket$()
			.pipe(
				filter((data) => data.list.length > 0),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: (planMeta: IProjectTicketListMetadata) => {
					self.spreadsheet.process<IProjectTicketListMetadata>(planMeta);
				},
				complete: () => {
					self.spreadsheet.complete();
					analyticsEventsService.exportMasterSchedule();
					this.updateTickets();
				},
			});
	}

	// download the spreadsheet, this prompts user to browser native download
	public download(filename: string): void {
		this.spreadsheet.export(filename);
	}

	// destroy api and all its internal data
	public destroy(): void {
		if (this.spreadsheet) {
			this.spreadsheet.destroy();
			this.exception$.complete();
			this.processComplete$.complete();
			this.destroy$.next();
			this.destroy$.complete();
			this.spreadsheet = null;
			this.exception$ = null;
			this.processComplete$ = null;
			this.destroy$ = null;
		}
	}

	private getTicket$(): Observable<IProjectTicketListMetadata> {
		const projectId = this.projectState.selectedProjectSync.getProjectId();
		return this.projectTicketsService
			.requestProjectTickets(projectId)
			.pipe
			// The below map is the old milestone only filter
			// map((planMeta) => {
			// 	planMeta.list = planMeta
			// 		? planMeta.list.filter((ticket: TicketFields) => ticket.type === TicketType.Milestone)
			// 		: [];
			// 	return planMeta;
			// })
			();
	}

	private updateTickets(): void {
		const promises: Array<Promise<void>> = [];

		this.ticketsToUpdate.forEach((data: IExternalScheduleTicketData) => {
			// null check so we don't wipe out database
			if (data.projectId && data.planId && data.ticketId) {
				const ticketPath = fancyFirebaseGetters.getTicketPath(data.projectId, data.planId, data.ticketId);
				promises.push(
					fancyFirebaseReferencer
						.getLegacyRef<firebase.database.Reference>(ticketPath, data.projectId)
						.child('externalScheduleFinishDate')
						.set(dayjs(data.finishDate).format(dateTimeFormat))
				);
			}
		});

		Promise.all(promises).catch((error) =>
			this.logger.log(`External schedule ticket field externalScheduleFinishDate update error: ${error}`)
		);
	}

	private formatMissingIdMessage(data: object): string {
		return `${dayjs().format(this.EXCEPTION_DATE_FORMAT)} Plan "${this.getPlanName(
			(data as any).planId
		)}" Ticket "${(data as any).activityName}" Scheduling ID "<a href='${
			(data as any).ticketUrl
		}' target="_blank">${(data as any).externalScheduleId}</a>" could not be matched`;
	}

	private formatDuplicateIdMessage(data: object): string {
		return `${dayjs().format(this.EXCEPTION_DATE_FORMAT)} Plan "${this.getPlanName(
			(data as any).planId
		)}" Ticket "${(data as any).activityName}" Scheduling ID "<a href='${
			(data as any).ticketUrl
		}' target="_blank">${(data as any).externalScheduleId}</a>" is duplicated`;
	}

	private getPlanName(planId: string): string {
		return this.projectState.plansApi.data.syncList.get(planId).view.name;
	}
}
